// Universal capture for failed network requests.
//
// The single place that maps a fetch transport error onto a consistent Sentry tag taxonomy,
// so the GlitchTip dashboard can answer at a glance:
//   - which API     (the `api` tag — the owning GitHub repo / microservice)
//   - which HTTP method / endpoint / response status / error code
//
// Call sites import `@sentry/vue` directly for everything else; this helper is the one shared
// abstraction, because network errors are the high-volume, taxonomy-heavy case worth
// standardising. med-web-account is fetch-only (one `authedJson` transport, no axios), so —
// unlike med-web-funnel's `src/modules/sentry/network.ts` — only the fetch builder is ported
// (the axios context builder is dropped).
import * as Sentry from '@sentry/vue'

type ApiTransport = 'fetch'
type ApiLevel = 'error' | 'warning'

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === 'object' && value !== null
}

/**
 * Normalised view of a structured API error body, shared across med-* services. The two
 * flavours seen in the wild are the rich envelope ({ code, error_code, message, trace_id })
 * and the thin documented shape ({ message }) — see docs/architecture/backend-apis.md.
 */
interface ApiErrorEnvelope {
	/** Symbolic application error code, e.g. 'CONFLICT' — preferred for the tag. */
	errorCode?: string
	/** Numeric application code (often mirrors the HTTP status). */
	code?: number
	/** Developer-facing message; PII-scrubbed before it reaches Sentry. */
	message?: string
	/** Server-side correlation id for cross-referencing the service's own logs. */
	traceId?: string
}

export interface ApiErrorContext {
	/** Logical microservice name (owning GitHub repo) — headline tag for finding the team. */
	api: string
	method?: string
	/** Raw URL or path; templated before tagging to keep tag cardinality sane. */
	endpoint?: string
	/** HTTP status, or 'network' when there is no response (transport failure). */
	status?: number | 'network'
	/** App-level code from the response body, or transport code, else 'unknown'. */
	errorCode?: string
	/** Parsed structured body — surfaced as the `api_error` context (message scrubbed). */
	errorBody?: ApiErrorEnvelope
	transport: ApiTransport
	/** Override the level otherwise derived from status. */
	level?: ApiLevel
	/** User-flow context, e.g. 'subscription'. */
	flow?: string
	/** Sub-step within the flow, e.g. 'cancel'. */
	step?: string
	/** Extra structured data attached to the event. */
	extra?: Record<string, unknown>
}

/**
 * A UUID path segment, templated to `{id}` below. Exported so the orders store, which only sends an
 * order id that this matches, checks with the same pattern — an id it sends never lands in the tag raw.
 * Shared, so no `g` / `y` flag: those make `.test()` carry `lastIndex` from one caller to the next.
 */
export const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** Strip origin/query and replace id-like segments so endpoints group cleanly. */
function templateEndpoint(endpoint?: string): string {
	if (!endpoint) return 'unknown'
	let path = endpoint.split('?')[0].split('#')[0]
	if (/^https?:\/\//i.test(path)) {
		try {
			path = new URL(path).pathname
		} catch {
			/* keep raw path */
		}
	}
	const templated = path
		.split('/')
		.map((seg) => (UUID_RE.test(seg) || /^\d+$/.test(seg) ? '{id}' : seg))
		.join('/')
	return templated || '/'
}

/**
 * Pull the structured fields out of an error response body. Reads the rich envelope and the
 * thin { message } shape; a non-JSON string body becomes the message. Symbolic `error_code` is
 * kept separate from the numeric `code` so the tag can prefer the former (a symbolic code is far
 * more useful than a number that just mirrors `api.status`).
 *
 * `code` itself is symbolic in med-orders and med-users ({ code: 'not_found' }), numeric in
 * med-billing-engine ({ code: 409, error_code: 'CONFLICT' }). A non-numeric string `code` is
 * therefore the symbolic code when the body has no `error_code` / `reason_code` / `error` — so a
 * 404 `not_found` and a 500 `internal_error` on the same endpoint differ by more than `api.status`.
 */
function parseApiErrorBody(body: unknown): ApiErrorEnvelope {
	if (typeof body === 'string') return { message: body }
	if (!isRecord(body)) return {}
	const code =
		typeof body.code === 'number'
			? body.code
			: typeof body.code === 'string' && /^\d+$/.test(body.code)
				? Number(body.code)
				: undefined
	const errorCode =
		typeof body.error_code === 'string'
			? body.error_code
			: typeof body.reason_code === 'string'
				? body.reason_code
				: typeof body.error === 'string'
					? body.error
					: typeof body.code === 'string' && body.code !== '' && code === undefined
						? body.code
						: undefined
	const message = typeof body.message === 'string' ? body.message : undefined
	const traceId =
		typeof body.trace_id === 'string' ? body.trace_id : typeof body.traceId === 'string' ? body.traceId : undefined
	return { errorCode, code, message, traceId }
}

/** Tag value for `api.error_code`: symbolic code wins, then numeric, then transport code, else 'unknown'. */
function resolveErrorCodeTag(envelope: ApiErrorEnvelope, transportCode?: string): string {
	return (
		envelope.errorCode ??
		(envelope.code != null ? String(envelope.code) : undefined) ??
		(transportCode || undefined) ??
		'unknown'
	)
}

function resolveLevel(status: number | 'network' | undefined, override?: ApiLevel): ApiLevel {
	if (override) return override
	if (status === 'network' || status == null) return 'error'
	// 5xx and transport failures are real incidents; 4xx are usually client/expected.
	return status >= 500 ? 'error' : status >= 400 ? 'warning' : 'error'
}

/**
 * Build a context for a fetch-based call. Used by the `authedJson` transport in
 * `src/api/http.ts`: flow/step ride along on the request options so a single event carries both
 * the network taxonomy and the user-flow context.
 */
export function buildFetchApiErrorContext(params: {
	api: string
	method?: string
	endpoint?: string
	status?: number | 'network'
	body?: unknown
	transportCode?: string
	errorCode?: string
	level?: ApiLevel
	flow?: string
	step?: string
	extra?: Record<string, unknown>
}): ApiErrorContext {
	const envelope = parseApiErrorBody(params.body)
	return {
		api: params.api,
		method: params.method,
		endpoint: params.endpoint,
		status: params.status ?? 'network',
		errorCode: params.errorCode ?? resolveErrorCodeTag(envelope, params.transportCode),
		errorBody: envelope,
		transport: 'fetch',
		level: params.level,
		flow: params.flow,
		step: params.step,
		extra: params.extra,
	}
}

// A free-text API message is the one field that could echo user input, so it gets a value-level
// PII pass before it reaches Sentry. `redactPii` (init.ts) is key-based and cannot touch a bare
// string, hence this separate scrub: emails and phone/long-digit runs are masked, then the result
// is truncated. Over-redaction is the safe direction for a medical app — error_code/trace_id carry
// the real signal; the message is only a human hint.
const EMAIL_RE = /[^\s@]+@[^\s@]+\.[^\s@]+/g
const LONG_DIGITS_RE = /\+?\d[\d\s().-]{5,}\d/g
const MAX_MESSAGE_LEN = 300

function scrubApiMessage(message: string): string {
	const scrubbed = message.replace(EMAIL_RE, '[email]').replace(LONG_DIGITS_RE, '[number]')
	return scrubbed.length > MAX_MESSAGE_LEN ? `${scrubbed.slice(0, MAX_MESSAGE_LEN)}…` : scrubbed
}

/** Build the `api_error` context card from a parsed body, scrubbing the message. */
function buildApiErrorContextEntry(body: ApiErrorEnvelope): Record<string, unknown> | undefined {
	const entry: Record<string, unknown> = {}
	if (body.errorCode != null) entry.error_code = body.errorCode
	if (body.code != null) entry.code = body.code
	if (body.traceId != null) entry.trace_id = body.traceId
	if (body.message != null) entry.message = scrubApiMessage(body.message)
	return Object.keys(entry).length > 0 ? entry : undefined
}

/**
 * Capture a failed network request with the shared tag taxonomy.
 *
 * Events are deduped by (api, method, endpoint, status) via an explicit fingerprint, so the
 * dashboard shows one issue per failing endpoint rather than one per raw error message. Pass
 * `error` as a thrown Error to keep the stack, or `null` for an expected non-2xx (e.g. a
 * warnStatus) that you still want recorded. A no-op until `Sentry.init()` has run — the
 * statistics-consent gate (src/sentry/init.ts) decides when that happens.
 */
export function captureApiError(error: unknown, ctx: ApiErrorContext): void {
	const endpoint = templateEndpoint(ctx.endpoint)
	const status = ctx.status ?? 'network'
	const method = ctx.method ?? 'UNKNOWN'
	const level = resolveLevel(ctx.status, ctx.level)

	Sentry.withScope((scope) => {
		scope.setLevel(level)
		scope.setTags({
			api: ctx.api,
			'api.method': method,
			'api.endpoint': endpoint,
			'api.status': String(status),
			'api.error_code': ctx.errorCode ?? 'unknown',
			'api.transport': ctx.transport,
		})
		if (ctx.flow) scope.setTag('flow', ctx.flow)
		if (ctx.step) scope.setTag('step', ctx.step)
		if (ctx.extra) scope.setExtras(ctx.extra)
		if (ctx.errorBody) {
			const apiError = buildApiErrorContextEntry(ctx.errorBody)
			if (apiError) scope.setContext('api_error', apiError)
		}
		scope.setFingerprint(['api-error', ctx.api, method, endpoint, String(status)])

		if (error instanceof Error) {
			scope.captureException(error)
		} else {
			scope.captureMessage(`[${ctx.api}] ${method} ${endpoint} → ${status}`)
		}
	})
}
